import {useQuery} from '@apollo/react-hooks';
import {ApolloError} from 'apollo-client';
import gql from 'graphql-tag';
import {useCallback, useState} from 'react';

import {propagateErrorsContext} from '../../util/errors';

export const PAGE_SIZE_IMPROVED_PAGINATION_MAX = 2_000;
export const PAGE_SIZE_MAX = 10_000;

///// Query definition

export const QUERY = gql`
  query RunLogLines(
    $projectName: String!
    $entityName: String
    $runName: String!
    $last: Int!
    $before: String
    $useImprovedPagination: Boolean = false
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      run(name: $runName) {
        id
        logLines(
          last: $last
          before: $before
          useImprovedPagination: $useImprovedPagination
        ) {
          edges {
            node {
              id
              label
              level
              line
              timestamp
            }
          }
          pageInfo {
            hasPreviousPage
            startCursor
          }
        }
      }
    }
  }
`;

interface Variables {
  entityName: string;
  projectName: string;
  runName: string;
  last: number;
  before?: string;
  useImprovedPagination: boolean;
}

interface Data {
  project: ProjectData;
}

interface ProjectData {
  run: RunData;
}

interface RunData {
  logLines: LogLines;
}

export interface LogLines {
  edges: Array<LogLineEdge>;
  pageInfo?: PageInfo;
}

export interface LogLineEdge {
  node: LogLineNode;
}

export interface PageInfo {
  hasPreviousPage: boolean;
  startCursor?: string;
}

export interface LogLineNode {
  id: string;
  label?: string;
  level: string;
  line: string;
  timestamp: string;
}

export interface UseRunLogQueryParams {
  entityName: string;
  pollInterval: number;
  projectName: string;
  runName: string;
  skip?: boolean;
  useImprovedPagination?: boolean;
}

export interface UseRunLogQueryResult {
  error?: ApolloError;
  loading: boolean;
  logLines: LogLines;
  // Backward pagination (older logs)
  fetchMorePrevious?: () => void;
  fetchMorePreviousError?: ApolloError;
  hasPrevious?: boolean;
  isLoadingPrevious?: boolean;
}

export function useRunLogQuery({
  entityName,
  pollInterval,
  projectName,
  runName,
  skip = false,
  useImprovedPagination = false,
}: UseRunLogQueryParams): UseRunLogQueryResult {
  const pageSize = useImprovedPagination
    ? PAGE_SIZE_IMPROVED_PAGINATION_MAX
    : PAGE_SIZE_MAX;

  const {data, loading, error, fetchMore} = useQuery<Data, Variables>(QUERY, {
    context: propagateErrorsContext(),
    pollInterval: useImprovedPagination ? undefined : pollInterval,
    skip,
    variables: {
      entityName,
      projectName,
      runName,
      last: pageSize,
      useImprovedPagination,
    },
  });

  const logLines: LogLines = data?.project?.run?.logLines ?? {edges: []};
  const startCursor = data?.project?.run?.logLines?.pageInfo?.startCursor;
  const hasPreviousPage = useImprovedPagination
    ? (data?.project?.run?.logLines?.pageInfo?.hasPreviousPage ?? false)
    : false;

  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [fetchMorePreviousError, setFetchMorePreviousError] = useState<
    ApolloError | undefined
  >();

  const fetchMorePrevious = useCallback(() => {
    if (skip || !hasPreviousPage || !startCursor || isLoadingPrevious) {
      return;
    }

    setIsLoadingPrevious(true);
    setFetchMorePreviousError(undefined);

    fetchMore({
      variables: {
        entityName,
        projectName,
        runName,
        last: pageSize,
        before: startCursor,
        useImprovedPagination: true,
      },
      updateQuery: (resultPrev, {fetchMoreResult: result}) => {
        const data = result?.project?.run?.logLines;
        if (!data || !data.edges.length) {
          return resultPrev;
        }

        const edges = data.edges;
        const edgesPrev = resultPrev.project.run.logLines.edges;

        return {
          ...result,
          project: {
            ...result.project,
            run: {
              ...result.project.run,
              logLines: {
                ...result.project.run.logLines,
                edges: [...edges, ...edgesPrev],
              },
            },
          },
        };
      },
    })
      .catch(setFetchMorePreviousError)
      .finally(() => setIsLoadingPrevious(false));
  }, [
    entityName,
    fetchMore,
    hasPreviousPage,
    isLoadingPrevious,
    pageSize,
    projectName,
    runName,
    skip,
    startCursor,
  ]);

  if (!useImprovedPagination) {
    return {
      loading,
      logLines,
      error,
    };
  }

  return {
    error,
    fetchMorePrevious,
    fetchMorePreviousError,
    hasPrevious: hasPreviousPage,
    isLoadingPrevious,
    loading,
    logLines,
  };
}
